import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { normaliseDecimal } from '@/features/finance/lines'
import type {
  Invoice,
  InvoiceHeaderPayload,
  InvoiceLinePayload,
  InvoiceSummary,
  NewInvoicePayload,
} from '@/features/finance/invoice'

/* The client invoices raised against one case (Story 5.4, FR-61).
 *
 * Every route is case-scoped, so every key hangs off the case's detail key —
 * the same place `legs`, `files` and `money` hang. That is not tidiness: the
 * row scope for an invoice IS the case's, so a cache entry that outlived the
 * case read it was authorised by would be a list nobody re-checks.
 *
 * NOTHING here is optimistic (UX-DR-24). An invoice is money: the figure that
 * appears is the one the server booked, at the rate the server resolved, after
 * the AD-10 guard has had its say. A cache flipped ahead of the response would
 * show a charge that was about to be refused — which is the precise failure
 * AC-7 exists to prevent, reintroduced on the client side.
 *
 * Every write answers with the WHOLE invoice, so each one seeds the detail
 * cache from the response rather than refetching: adding a line moves the
 * total, the USD equivalent and the billed figure the refusal message is
 * composed from, and three round trips to learn what one response already
 * carried is three chances to render a stale pair.
 */

/** The two keys, both hung off the case's detail key.
 *
 *  Built from `queryKeys.cases.detail` rather than spelled out, so the prefix
 *  cannot drift from the one the case hooks use — and so invalidating the case
 *  still reaches its invoices.
 *
 *  `invoice` singular rather than nesting the detail UNDER the list: a detail
 *  key beginning with the list key would be invalidated by every list refresh,
 *  which would refetch an invoice the write response has just delivered in
 *  full. Two siblings keep the seed below meaningful. */
const invoiceKeys = {
  list: (caseId: string) => [...queryKeys.cases.detail(caseId), 'invoices'] as const,
  /** The prefix every invoice detail on a case shares, so `seed` can reach the
   *  siblings of the one it was handed without spelling the shape twice. */
  details: (caseId: string) => [...queryKeys.cases.detail(caseId), 'invoice'] as const,
  detail: (caseId: string, invoiceId: string) =>
    [...invoiceKeys.details(caseId), invoiceId] as const,
}

/** Every invoice raised against one case.
 *
 *  A list with no cap: FR-61 invoices a case in payment tranches, and several
 *  rows legitimately share one case. */
export function useCaseInvoices(caseId: string) {
  return useQuery<InvoiceSummary[]>({
    queryKey: invoiceKeys.list(caseId),
    // No case chosen yet is not an empty case: firing `/cases//invoices` would
    // ask the server a question about nothing and render its 404 as a failure.
    enabled: caseId !== '',
    queryFn: async () =>
      (await api.get<{ invoices: InvoiceSummary[] }>(`/cases/${caseId}/invoices`))
        .invoices,
  })
}

/** One invoice with its lines — what the editor reads. */
export function useInvoice(caseId: string, invoiceId: string) {
  return useQuery<Invoice>({
    queryKey: invoiceKeys.detail(caseId, invoiceId),
    enabled: caseId !== '' && invoiceId !== '',
    queryFn: () => api.get<Invoice>(`/cases/${caseId}/invoices/${invoiceId}`),
  })
}

/** Opens a draft invoice, prefilled server-side from the accepted revision's
 *  PRINTED lines (AC-1, AD-15).
 *
 *  The response is the stored invoice, including the rate it resolved and the
 *  lines it prefilled — which is why nothing is guessed here. The client cannot
 *  know either, and inventing them would put a figure on screen that no
 *  database row holds. */
export function useRaiseInvoice(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation<Invoice, Error, NewInvoicePayload>({
    mutationFn: ({ trancheId, ...header }) =>
      api.post<Invoice>(`/cases/${caseId}/invoices`, {
        ...header,
        /* Written out rather than spread through, and ALWAYS sent — blank
           included. The server reads an absent key and an empty string as the
           same answer ("outside the series"), so this changes no request that
           was already correct; what it changes is that the field is now visible
           at the one place it is decided. A caller that forgets it raises an
           invoice that displaces no tranche, and the case then reports the same
           money owed twice — a defect nothing on screen announces. */
        trancheId: trancheId ?? '',
      }),
    // Through `seed`, not through a seed-and-invalidate-the-list of its own.
    // A raise is a case-wide money event like any other: the prefill from the
    // accepted offer (AC-1) puts lines on the new invoice, which moves
    // `billedUsd` — a CASE-wide figure — on every other draft's cached detail.
    // Left stale, another tranche opened within staleTime composes its AD-10
    // refusal from a billed figure that is one invoice out of date.
    onSuccess: (invoice) => seed(queryClient, caseId, invoice),
  })
}

/** Edits a draft's header — the invoice date, the client's own reference and
 *  whether tax applies. The currency and the economic date are absent from the
 *  payload on purpose (AD-9); see `InvoiceHeaderPayload`. */
export function useAmendInvoice(caseId: string, invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation<Invoice, Error, InvoiceHeaderPayload, number>({
    mutationFn: ({ trancheId, ...header }) =>
      api.put<Invoice>(`/cases/${caseId}/invoices/${invoiceId}`, {
        ...header,
        /* Sent on every save, and here the omission is not harmless: the PUT is
           a whole-header replace and a missing `trancheId` UNLINKS the
           instalment (see `InvoiceHeaderPayload`). A caller that hands this
           mutation no tranche is therefore stating "outside the series"
           deliberately — which is why the field is spelled out here rather than
           left to arrive, or not arrive, inside a spread. */
        trancheId: trancheId ?? '',
      }),
    onMutate: () => claimWrite(caseId, invoiceId),
    onSuccess: (invoice, _payload, ticket) => seed(queryClient, caseId, invoice, ticket),
  })
}

/** Adds or edits one charge — the write the AD-10 guard fires on (AC-7).
 *
 *  ONE mutation for both verbs rather than two, because the surface has one
 *  charge form and the refusal has to land on it. Two mutations would mean two
 *  error states, and the editor would have to remember which of them last
 *  spoke — which is how a refusal ends up displayed under the wrong row, or
 *  not at all. */
export function useWriteInvoiceLine(caseId: string, invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation<Invoice, Error, InvoiceLinePayload & { lineId?: string }, number>({
    mutationFn: ({ lineId, ...draft }) => {
      /* Normalised for the separators a Russian keyboard produces, and still a
         string. The server's money.D refuses a JSON number outright, so a
         client that computed this in floats lands in a bind error rather than
         quietly billing one — but arriving as text is this side's job. */
      const payload = {
        description: draft.description.trim(),
        quantity: normaliseDecimal(draft.quantity),
        unitPrice: normaliseDecimal(draft.unitPrice),
        amount: normaliseDecimal(draft.amount),
      }
      const base = `/cases/${caseId}/invoices/${invoiceId}/lines`
      return lineId === undefined
        ? api.post<Invoice>(base, payload)
        : api.put<Invoice>(`${base}/${lineId}`, payload)
    },
    onMutate: () => claimWrite(caseId, invoiceId),
    onSuccess: (invoice, _payload, ticket) => seed(queryClient, caseId, invoice, ticket),
  })
}

/** Removes one charge. Answers with the invoice rather than 204, so the totals
 *  a removal moves arrive with it. */
export function useDropInvoiceLine(caseId: string, invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation<Invoice, Error, string, number>({
    mutationFn: (lineId) =>
      api.delete<Invoice>(`/cases/${caseId}/invoices/${invoiceId}/lines/${lineId}`),
    onMutate: () => claimWrite(caseId, invoiceId),
    onSuccess: (invoice, _lineId, ticket) => seed(queryClient, caseId, invoice, ticket),
  })
}

/** Discards a draft invoice.
 *
 *  Refused by the server on an issued one (AD-24), and refused again by the
 *  table's own trigger underneath it, so nothing here needs to guess at the
 *  state: the caller offers the action where it makes sense and the refusal
 *  arrives in words when it does not.
 *
 *  Invalidates rather than removes. Dropping the row from the cache by hand
 *  would be this file deciding what the case's paper now consists of, and the
 *  list is a server answer: an invoice whose delete raced somebody else's issue
 *  is still there, and the refetch is what says so.
 *
 *  The DETAIL key is dropped, though, and that is not the same decision: the
 *  page that was showing this invoice is about to unmount, and a cached detail
 *  under a dead id would be served to the next reader who reaches that route
 *  before the refetch answers — a document that no longer exists, rendered from
 *  memory. */
export function useDropInvoice(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (invoiceId) => api.delete<void>(`/cases/${caseId}/invoices/${invoiceId}`),
    onSuccess: (_void, invoiceId) => {
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(caseId, invoiceId) })
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.list(caseId) })
      // The case's own money moved: a discarded draft's charges leave the
      // billed total, which is what every other invoice's ceiling is measured
      // against.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
    },
  })
}

/** The invoice as the page it will print — HTML composed by the server.
 *
 *  A QUERY rather than a mutation, because it reads: nothing is stored, nothing
 *  is minted, and asking twice costs a render. `enabled` is what makes it lazy,
 *  so a table of twenty invoices renders twenty rows and no previews until
 *  somebody opens one.
 *
 *  NOT cached beyond the viewing. `staleTime: 0` and a short garbage-collection
 *  window are deliberate: the draft underneath changes on every charge written,
 *  and a preview served from memory would show a document that has since moved
 *  — which is the exact failure the hand-drawn approximation it replaces used to
 *  have, arrived at by a different route. */
export function useInvoicePreview(caseId: string, invoiceId: string, enabled: boolean) {
  return useQuery<string>({
    queryKey: [...invoiceKeys.detail(caseId, invoiceId), 'preview'],
    enabled: enabled && caseId !== '' && invoiceId !== '',
    staleTime: 0,
    gcTime: 30_000,
    queryFn: async () =>
      (
        await api.get<{ html: string }>(
          `/cases/${caseId}/invoices/${invoiceId}/preview`,
        )
      ).html,
  })
}

/* ── The ordering guard ──────────────────────────────────────────────────────
 *
 * Every write answers with the WHOLE invoice and seeds it into the detail key,
 * which is what makes one round trip enough. It is also what makes RESPONSE
 * ORDER matter: two charges added in quick succession are two requests in
 * flight at once, and nothing guarantees the second answer arrives second. If
 * the first response lands last, it writes an invoice missing the second charge
 * into the cache and it stays there until something unrelated invalidates it —
 * a screen showing a line the server has, and totals that do not include it.
 *
 * So each write takes a ticket before it goes out, and only the LATEST ticket
 * for an invoice is allowed to seed. A late response invalidates the detail
 * instead, which refetches the truth rather than guessing at it — the same
 * stance the rest of this file takes about money (UX-DR-24).
 *
 * Module state rather than a ref, because the racing mutations are frequently
 * two different component instances — the editor's own form and a row's remove
 * button — and a per-component counter would not see the other. */
let writeSequence = 0
const latestWrite = new Map<string, number>()

function writeKeyOf(caseId: string, invoiceId: string): string {
  return invoiceKeys.detail(caseId, invoiceId).join('/')
}

/** Claims the next ticket for one invoice, and records it as the latest. */
function claimWrite(caseId: string, invoiceId: string): number {
  const ticket = ++writeSequence
  latestWrite.set(writeKeyOf(caseId, invoiceId), ticket)
  return ticket
}

/** Writes the served invoice into its own key and refreshes everything the
 *  write moved beside it.
 *
 *  The list carries the same totals as the detail, so refreshing only the one
 *  that was written would leave the row behind the sheet disagreeing with the
 *  sheet in front of it.
 *
 *  The SIBLING details go too, and they are the subtle half. `billedUsd` and
 *  the headroom derived from it are CASE-wide figures — they sum every invoice
 *  on the case — so a line added here moves them on every other draft's cached
 *  detail as well. Left alone, another tranche opened within staleTime would
 *  compose an AD-10 refusal from a billed figure that is one line out of date,
 *  and name an excess that is not the one the server judged. Invalidated rather
 *  than patched: the server is the only place that arithmetic is authoritative
 *  (UX-DR-24). */
function seed(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
  invoice: Invoice,
  /** The ticket this response's write claimed, when it came from one. A raise
   *  passes none: its invoice did not exist before the response, so nothing
   *  can have overtaken it. */
  ticket?: number,
) {
  const detail = invoiceKeys.detail(caseId, invoice.id)
  const overtaken =
    ticket !== undefined && latestWrite.get(writeKeyOf(caseId, invoice.id)) !== ticket

  if (overtaken) {
    // A later write is in flight or has already answered, so this payload is
    // the older picture of the two. Refetched rather than seeded.
    void queryClient.invalidateQueries({ queryKey: detail })
  } else {
    queryClient.setQueryData(detail, invoice)
  }
  void queryClient.invalidateQueries({ queryKey: invoiceKeys.list(caseId) })
  const siblings = invoiceKeys.details(caseId)
  void queryClient.invalidateQueries({
    // Every invoice detail on THIS case except the one just served, which
    // already holds the response the server sent a moment ago.
    predicate: ({ queryKey }) =>
      queryKey.length === siblings.length + 1 &&
      siblings.every((part, i) => queryKey[i] === part) &&
      queryKey[siblings.length] !== invoice.id,
  })
}
